// Lectura de coordenadas pegadas a mano. El coordinador copia el enlace de Google Maps
// desde el celular y lo pega en el formulario del PDV: aquí se saca la lat/lng sin
// llamar a ningún servicio externo (los enlaces cortos maps.app.goo.gl NO se pueden
// resolver sin red, así que devuelven null y el usuario debe abrirlos y copiar el largo).

export interface LatLng {
  lat: number;
  lng: number;
}

/** Un número decimal con signo, tal como aparece en los enlaces de Maps. */
const NUM = String.raw`(-?\d+(?:\.\d+)?)`;

/** Formas conocidas, en orden de confianza. La primera que coincida manda. */
const PATTERNS: RegExp[] = [
  // 1. Coordenada EXACTA del lugar dentro del bloque `data=` (…!8m2!3d-12.1268!4d-77.0287).
  //    Va primero: el enlace largo de "compartir" también trae el @… del encuadre, que
  //    apunta al centro de la pantalla y no a la tienda.
  new RegExp(`!3d${NUM}!4d${NUM}`),
  // 2. Parámetros explícitos: ?q=, ?query=, ?ll=, ?center=
  new RegExp(`[?&](?:q|query|ll|center)=\\s*${NUM}\\s*,\\s*${NUM}`, 'i'),
  // 3. Encuadre del mapa: /@-12.1268,-77.0287,17z
  new RegExp(`@\\s*${NUM}\\s*,\\s*${NUM}`),
  // 4. Coordenadas sueltas pegadas tal cual: "-12.1268, -77.0287"
  new RegExp(`${NUM}\\s*,\\s*${NUM}`),
];

/** Rango WGS84. Fuera de esto la coordenada no existe y no debe guardarse. */
function enRango(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/**
 * Extrae lat/lng de un enlace de Google Maps (o de un par de coordenadas pegadas).
 * Devuelve null si no encuentra nada o si los valores están fuera de rango:
 * más vale dejar el campo vacío que guardar una coordenada inventada, porque una
 * coordenada mala genera alertas falsas de "lejos del PDV".
 */
export function parseGoogleMapsUrl(url: string): LatLng | null {
  if (typeof url !== 'string') return null;
  const raw = url.trim();
  if (raw === '') return null;

  // Los enlaces copiados suelen traer la coma como %2C.
  let texto = raw;
  try {
    texto = decodeURIComponent(raw);
  } catch {
    texto = raw;
  }

  for (const re of PATTERNS) {
    const m = re.exec(texto);
    if (!m) continue;
    const lat = Number.parseFloat(m[1]);
    const lng = Number.parseFloat(m[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return enRango(lat, lng) ? { lat, lng } : null;
  }
  return null;
}
